import { SentryFeatureTags, logException } from "@punchup/sentry";

import type { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import dayjs from "@punchup/utils/dayjs";

import type { TixologiEventSnapshot } from "../../models/shows";
export type { TixologiEventSnapshot };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TixologiBulkResponse {
  success: { id: number }[] | null;
  errors: { event_id: number; error: string }[] | null;
  summary: { total: number; succeeded: number; failed: number };
}

export interface TixologiUpdatePayload {
  event_ids: number[];
  name?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  door_time?: string | null;
  image_url?: string;
}

/** Fields the caller wants to change on each show. */
export interface ShowUpdateFields {
  name?: string;
  description?: string;
  start_time?: string; // HH:MM (24h) — updates time portion of datetime
  end_time?: string; // HH:MM (24h) — Tixologi only
  door_time?: string | null; // HH:MM (24h) — Tixologi only
  poster_img?: string | null; // Cloudflare image ID
  image_url?: string; // Full image URL for Tixologi
  date?: string; // YYYY-MM-DD — updates date portion of datetime
  comedianIds?: string[]; // Ordered final lineup applied to every show; [] clears it, undefined leaves it untouched
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function isValidTimeFormat(time: string): boolean {
  const parts = time.split(":");
  const [hours, minutes] = parts.map(Number);
  return (
    parts.length === 2 &&
    !isNaN(hours) &&
    !isNaN(minutes) &&
    hours >= 0 &&
    hours <= 23 &&
    minutes >= 0 &&
    minutes <= 59
  );
}

export function isValidDateFormat(date: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(date) && dayjs(date, "YYYY-MM-DD").isValid()
  );
}

// ---------------------------------------------------------------------------
// Tixologi operations
// ---------------------------------------------------------------------------

/**
 * Calls the Tixologi bulk update endpoint. Does NOT throw on partial failure —
 * the caller inspects `errors` to decide what to do.
 */
export async function callTixologiBulkUpdate(
  token: string,
  payload: TixologiUpdatePayload
): Promise<TixologiBulkResponse> {
  // eslint-disable-next-line no-fetch-for-local-paths/no-fetch-for-local-paths-rule
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/events/bulk`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Tixologi bulk update failed: ${res.status} ${body}`);
  }

  return (await res.json()) as TixologiBulkResponse;
}

/**
 * Applies tag additions/removals to each Tixologi event individually (Tixologi
 * has no bulk tag endpoint). Best-effort and non-atomic: a failure on one event
 * is logged and counted but does not throw or affect the others. Mirrors the
 * single-show tag contract (`POST`/`DELETE /events/{id}/tags` with `{ tag_ids }`).
 */
export async function applyTixologiTagChanges(
  token: string,
  eventIds: number[],
  tagsToAdd: number[],
  tagsToRemove: number[],
  sentryPrefix: string
): Promise<{ succeeded: number; failed: number; failedEventIds: number[] }> {
  if (
    eventIds.length === 0 ||
    (tagsToAdd.length === 0 && tagsToRemove.length === 0)
  ) {
    return { succeeded: 0, failed: 0, failedEventIds: [] };
  }

  const apiUrl = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const perEventResults = await Promise.all(
    eventIds.map(async (eventId) => {
      try {
        if (tagsToAdd.length > 0) {
          // eslint-disable-next-line no-fetch-for-local-paths/no-fetch-for-local-paths-rule
          const res = await fetch(`${apiUrl}/events/${eventId}/tags`, {
            method: "POST",
            headers,
            body: JSON.stringify({ tag_ids: tagsToAdd }),
          });
          if (!res.ok) {
            throw new Error(
              `Tixologi add tags failed: ${res.status} ${await res.text()}`
            );
          }
        }
        if (tagsToRemove.length > 0) {
          // eslint-disable-next-line no-fetch-for-local-paths/no-fetch-for-local-paths-rule
          const res = await fetch(`${apiUrl}/events/${eventId}/tags`, {
            method: "DELETE",
            headers,
            body: JSON.stringify({ tag_ids: tagsToRemove }),
          });
          if (!res.ok) {
            throw new Error(
              `Tixologi remove tags failed: ${res.status} ${await res.text()}`
            );
          }
        }
        return { eventId, ok: true };
      } catch (error) {
        logException(error, {
          extra: { eventId, tagsToAdd, tagsToRemove },
          tags: { feature: SentryFeatureTags.VenueManager },
          data: `${sentryPrefix} Tixologi tag update failed. eventId=${eventId}`,
        });
        return { eventId, ok: false };
      }
    })
  );

  const failedEventIds = perEventResults
    .filter((r) => !r.ok)
    .map((r) => r.eventId);

  return {
    succeeded: perEventResults.filter((r) => r.ok).length,
    failed: failedEventIds.length,
    failedEventIds,
  };
}

/**
 * Builds Tixologi bulk-update payloads that revert events to their snapshot
 * state. Only reverts the events in `eventIdsToRevert`.
 */
export function buildRollbackPayload(
  snapshots: TixologiEventSnapshot[],
  eventIdsToRevert: number[]
): {
  event_ids: number[];
  name?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  door_time?: string | null;
}[] {
  const revertSet = new Set(eventIdsToRevert);
  return snapshots
    .filter((s) => revertSet.has(s.event_id))
    .map((s) => ({
      event_ids: [s.event_id],
      name: s.name,
      description: s.description,
      start_time: dayjs.utc(s.start_time).format("HH:mm"),
      end_time: dayjs.utc(s.end_time).format("HH:mm"),
      door_time: s.door_time ? s.door_time.substring(0, 5) : null,
    }));
}

/**
 * Attempts to revert Tixologi changes using snapshot data. Returns whether
 * all rollbacks succeeded.
 */
export async function compensateTixologi(
  token: string,
  snapshots: TixologiEventSnapshot[],
  succeededEventIds: number[],
  venuePageId: string,
  punchupError: unknown,
  originalUpdates: ShowUpdateFields,
  sentryPrefix: string
): Promise<boolean> {
  const rollbackPayloads = buildRollbackPayload(snapshots, succeededEventIds);

  let rollbackSucceeded = true;

  for (const payload of rollbackPayloads) {
    try {
      await callTixologiBulkUpdate(token, payload);
    } catch (rollbackError) {
      rollbackSucceeded = false;
      logException(rollbackError, {
        extra: { payload },
        tags: { feature: SentryFeatureTags.VenueManager },
        data: `${sentryPrefix} CRITICAL: Tixologi rollback failed — events may be out of sync. eventIds=${JSON.stringify(succeededEventIds)} venuePageId=${venuePageId} punchupError=${String(punchupError)} eventSnapshots=${JSON.stringify(snapshots.filter((s) => succeededEventIds.includes(s.event_id)))} newValues=${JSON.stringify({ name: originalUpdates.name, description: originalUpdates.description, start_time: originalUpdates.start_time, end_time: originalUpdates.end_time, door_time: originalUpdates.door_time })}`,
      });
    }
  }

  if (rollbackSucceeded) {
    logException("Bulk edit rollback succeeded after Punchup failure", {
      extra: { rollbackPayloads },
      tags: { feature: SentryFeatureTags.VenueManager },
      data: `${sentryPrefix} Rollback succeeded. revertedEventIds=${JSON.stringify(succeededEventIds)} venuePageId=${venuePageId}`,
    });
  }

  return rollbackSucceeded;
}

// ---------------------------------------------------------------------------
// Punchup operations
// ---------------------------------------------------------------------------

/**
 * Fetches shows from the DB, applies the requested field updates, and upserts
 * them via the `create_update_shows_with_comedians` RPC. Returns the count of
 * updated shows or throws on failure.
 *
 * When `updates.date` is provided it replaces the date portion of each show's
 * datetime (used by the single-show endpoint). When `updates.start_time` is
 * provided it replaces the time portion.
 */
export async function updateShowsInPunchup(
  succeededShowIds: string[],
  updates: ShowUpdateFields,
  sentryPrefix: string,
  extraLogContext: Record<string, unknown> = {}
): Promise<{ updatedCount: number }> {
  const supabase = createSupabaseAdminClient();

  const { data: shows, error: fetchError } = await supabase
    .from("comedian_shows")
    .select("*")
    .in("id", succeededShowIds)
    .is("deleted_at", null);

  if (fetchError || !shows?.length) {
    const err = fetchError || new Error("No shows found");
    logException(err, {
      extra: { succeededShowIds, ...extraLogContext },
      tags: { feature: SentryFeatureTags.VenueManager },
      data: `${sentryPrefix} Punchup fetch failed. showIds=${JSON.stringify(succeededShowIds)}`,
    });
    throw err;
  }

  const updatedShows = shows.map((show) => {
    const { created_at: _, ...rest } = show;

    let updatedDatetime = rest.datetime;

    if (rest.datetime) {
      let dt = dayjs(rest.datetime);

      if (updates.date) {
        const [year, month, day] = updates.date.split("-").map(Number);
        dt = dt
          .year(year)
          .month(month - 1)
          .date(day);
      }

      if (updates.start_time) {
        const [hours, minutes] = updates.start_time.split(":").map(Number);
        dt = dt.hour(hours).minute(minutes).second(0);
      }

      if (updates.date || updates.start_time) {
        updatedDatetime = dt.toISOString();
      }
    }

    return {
      ...rest,
      ...(updates.name !== undefined && { title: updates.name }),
      ...(updates.description !== undefined && {
        metadata_text: updates.description,
      }),
      ...((updates.start_time !== undefined || updates.date !== undefined) && {
        datetime: updatedDatetime,
      }),
      ...(updates.poster_img !== undefined && {
        poster_img: updates.poster_img,
      }),
    };
  });

  // Existing (show, comedian) pairings, so we can preserve each comedian's
  // current `hidden_from_comedian_page` rather than overwriting it on bulk edit.
  const existingPairingKeys = new Set<string>();
  if (updates.comedianIds !== undefined) {
    const { data: existingPairings, error: pairingsError } = await supabase
      .from("show_comedian_pairings")
      .select("show_id, comedian_id")
      .in("show_id", succeededShowIds);

    if (pairingsError) {
      logException(pairingsError, {
        extra: { succeededShowIds, ...extraLogContext },
        tags: { feature: SentryFeatureTags.VenueManager },
        data: `${sentryPrefix} Punchup pairings fetch failed. showIds=${JSON.stringify(succeededShowIds)}`,
      });
      throw pairingsError;
    }

    for (const pairing of existingPairings ?? []) {
      existingPairingKeys.add(`${pairing.show_id}:${pairing.comedian_id}`);
    }
  }

  // When comedianIds is provided, replace each show's lineup with the same
  // ordered set (membership-locked on the client). An empty array clears the
  // lineup; `null` leaves existing pairings untouched (see the RPC).
  const showComedianPairings =
    updates.comedianIds !== undefined
      ? updatedShows.flatMap((show) =>
          updates.comedianIds!.map((comedian_id, ordering) => ({
            show_id: show.id,
            comedian_id,
            ordering,
            // Existing pairings omit the flag so the RPC preserves the
            // comedian's current visibility; new pairings default to hidden,
            // mirroring the venue-side single-show lineup editor.
            ...(!existingPairingKeys.has(`${show.id}:${comedian_id}`) && {
              hidden_from_comedian_page: true,
            }),
          }))
        )
      : null;

  const rpcPayload = {
    show_records: updatedShows,
    show_comedian_pairings: showComedianPairings,
    venue_page_show_pairings: null,
  };
  const { error: upsertError } = await supabase.rpc(
    "create_update_shows_with_comedians",
    rpcPayload
  );

  if (upsertError) {
    logException(upsertError, {
      extra: { rpcPayload, ...extraLogContext },
      tags: { feature: SentryFeatureTags.VenueManager },
      data: `${sentryPrefix} Punchup upsert failed. showIds=${JSON.stringify(succeededShowIds)}`,
    });
    throw upsertError;
  }

  return { updatedCount: updatedShows.length };
}

/**
 * Of `candidateIds`, which shows are ALREADY linked to this venue page (via
 * `venue_pages_shows`). The bulk-create review step uses this to tell a fresh
 * import ("will be created") from a re-import ("already created — will update"):
 * the import's deterministic show ids make a re-run an upsert, and this read is
 * what surfaces that to the operator. Page-scoped on purpose — `comedian_shows`
 * is global; the page link is what the import itself writes.
 * Queried in batches: `.in()` goes in the URL, and a few hundred UUIDs make it too long to send.
 */
const EXISTING_SHOW_ID_BATCH = 100;

export async function getExistingShowIdsForVenuePage(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  candidateIds: string[]
): Promise<string[]> {
  const existing: string[] = [];
  for (let i = 0; i < candidateIds.length; i += EXISTING_SHOW_ID_BATCH) {
    const { data, error } = await supabase
      .from("venue_pages_shows")
      .select("show_id")
      .eq("venue_page_id", venuePageId)
      .in("show_id", candidateIds.slice(i, i + EXISTING_SHOW_ID_BATCH));
    if (error) throw error;
    existing.push(...(data ?? []).map((row) => row.show_id));
  }
  return existing;
}
